import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, X } from "lucide-react";
import { EnsoMark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING = {
  role: "assistant",
  content:
    "Hello, I'm the Vagus Digital assistant. Ask me about websites, AI tools, SEO or what working together looks like.",
};

const QUICK = ["What do you do?", "How much is a website?", "Book a call"];

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
  );
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || streaming) return;
    setInput("");
    const history = messages
      .slice(-6)
      .filter((m) => m.role !== "error")
      .map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, history, session_id: sessionId.current }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const { delta } = JSON.parse(payload);
            if (delta) {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                copy[copy.length - 1] = { ...last, content: last.content + delta };
                return copy;
              });
            }
          } catch {
            /* ignore malformed chunk */
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "error", content: "Connection dropped. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.2, duration: 0.5 }}
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-open-button"
        aria-label={open ? "Close chat" : "Open chat"}
        className="fixed bottom-6 right-6 z-[80] flex h-14 w-14 items-center justify-center rounded-full bg-jade text-paper shadow-ambient-lg transition-colors duration-300 hover:bg-ink"
      >
        {open ? <X className="h-5 w-5" /> : <EnsoMark className="h-7 w-7 text-paper" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-[80] flex h-[520px] w-[360px] max-w-[calc(100vw-3rem)] flex-col overflow-hidden rounded-2xl bg-white shadow-ambient-lg"
            data-testid="chat-panel"
          >
            <div className="flex items-center gap-3 border-b border-jade/10 bg-paper2/60 px-5 py-4">
              <EnsoMark className="h-5 w-5 text-jade" />
              <div>
                <p className="font-display text-lg font-medium leading-none text-ink">
                  Ask Vagus
                </p>
                <p className="mt-1 text-xs text-ink2/70">
                  Usually replies in seconds
                </p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "ml-auto bg-jade text-paper"
                      : m.role === "error"
                      ? "bg-red-700/10 text-red-700"
                      : "bg-paper2 text-ink"
                  }`}
                  data-testid={`chat-message-${i}`}
                >
                  {m.content || "…"}
                </div>
              ))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-2" data-testid="chat-quick-prompts">
                  {QUICK.map((q) => (
                    <button
                      key={q}
                      onClick={() => send(q)}
                      data-testid={`chat-quick-${q.slice(0, 8).toLowerCase().replace(/\s+/g, "-")}`}
                      className="rounded-full border border-jade/25 px-3.5 py-1.5 text-xs font-medium text-jade transition-colors duration-300 hover:bg-jade/5"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 border-t border-jade/10 px-4 py-3"
              data-testid="chat-form"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                data-testid="chat-input"
                placeholder="Ask about your project…"
                maxLength={1000}
                className="w-full rounded-full border border-jade/20 bg-paper/60 px-4 py-2.5 text-sm text-ink placeholder:text-ink2/40 transition-colors duration-300"
              />
              <button
                type="submit"
                disabled={streaming || !input.trim()}
                data-testid="chat-send-button"
                aria-label="Send message"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-jade text-paper transition-colors duration-300 hover:bg-ink disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;